// "A receber": o que os contratantes ainda devem das cargas e os
// comprovantes que precisam ir pelo Correio. Conta pura (a tela e o cartão
// do Início usam).
//   · adiantamento não recebido: vence no carregamento;
//   · saldo não recebido: vence na descarga (ou no carregamento, sem
//     descarga) mais o prazo do saldo do cliente (padrão: 30 dias).
import { diasEntre } from './datas.js';
import { somarDias } from './despesas.js';
import { dinheiro } from './formato.js';
import { comContas } from './fretes.js';

export const PRAZO_PADRAO_DO_SALDO = 30;

const centavos = (v) => Math.round((Number(v) || 0) * 100);

// Itens a receber: { tipo: 'adiantamento' | 'saldo', id, frete, cliente, valor, vencimento }
export function itensAReceber(fretes, clientes = []) {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const itens = [];
  for (const bruto of fretes) {
    if (bruto.deleted_at) continue;
    const f = comContas(bruto);
    const cliente = porId.get(f.cliente_id) ?? null;
    if (centavos(f.adto_valor) > 0 && !f.adto_recebido_em) {
      itens.push({ tipo: 'adiantamento', id: `${f.id}:adiantamento`, frete: f, cliente, valor: Number(f.adto_valor), vencimento: f.data_carregamento });
    }
    if (centavos(f.saldo_valor) > 0 && !f.saldo_recebido_em) {
      const prazo = Number.isInteger(cliente?.prazo_saldo_dias) ? cliente.prazo_saldo_dias : PRAZO_PADRAO_DO_SALDO;
      itens.push({
        tipo: 'saldo', id: `${f.id}:saldo`, frete: f, cliente, valor: Number(f.saldo_valor),
        vencimento: somarDias(f.data_descarga ?? f.data_carregamento, prazo),
      });
    }
  }
  return itens.sort((a, b) => (a.vencimento < b.vencimento ? -1 : a.vencimento > b.vencimento ? 1 : 0));
}

// Separados em atrasados, próximos 7 dias e depois, com os totais.
export function separarAReceber(itens, hoje) {
  const daqui7 = somarDias(hoje, 7);
  const grupos = { atrasados: [], semana: [], depois: [] };
  for (const item of itens) {
    if (item.vencimento < hoje) grupos.atrasados.push(item);
    else if (item.vencimento <= daqui7) grupos.semana.push(item);
    else grupos.depois.push(item);
  }
  const soma = (lista) => lista.reduce((s, x) => s + centavos(x.valor), 0) / 100;
  return {
    ...grupos,
    totais: { atrasados: soma(grupos.atrasados), semana: soma(grupos.semana), depois: soma(grupos.depois), tudo: soma(itens) },
  };
}

// Recebidos de `desde` em diante (para desfazer um "Recebi" por engano).
export function recebidosDesde(fretes, clientes, desde) {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const itens = [];
  for (const bruto of fretes) {
    if (bruto.deleted_at) continue;
    const f = comContas(bruto);
    const cliente = porId.get(f.cliente_id) ?? null;
    if (f.adto_recebido_em && f.adto_recebido_em >= desde && centavos(f.adto_valor) > 0) {
      itens.push({ tipo: 'adiantamento', id: `${f.id}:adiantamento`, frete: f, cliente, valor: Number(f.adto_valor), recebido_em: f.adto_recebido_em });
    }
    if (f.saldo_recebido_em && f.saldo_recebido_em >= desde && centavos(f.saldo_valor) > 0) {
      itens.push({ tipo: 'saldo', id: `${f.id}:saldo`, frete: f, cliente, valor: Number(f.saldo_valor), recebido_em: f.saldo_recebido_em });
    }
  }
  return itens.sort((a, b) => (a.recebido_em < b.recebido_em ? 1 : a.recebido_em > b.recebido_em ? -1 : 0));
}

// Comprovantes: os que precisam ser postados e os postados que ainda não chegaram.
export function comprovantes(fretes) {
  const vivos = fretes.filter((f) => !f.deleted_at);
  const porData = (a, b) => (a.data_carregamento < b.data_carregamento ? -1 : a.data_carregamento > b.data_carregamento ? 1 : 0);
  return {
    aPostar: vivos.filter((f) => f.comprovante_status === 'a_postar').sort(porData),
    postados: vivos.filter((f) => f.comprovante_status === 'postado').sort(porData),
  };
}

// ---------------------------------------------------------------------
// Placar de quem paga em dia (0.17.0): para cada cliente (ou agenciador), os
// saldos pagos nos últimos 12 meses — quantos, em quantos dias depois da
// descarga (ou do carregamento, sem descarga), em média, e quantos dentro do
// prazo do cliente — e os saldos atrasados agora. `por`: 'cliente_id' ou
// 'agenciador_id'. Só os saldos (o adiantamento vence no carregamento).
// ---------------------------------------------------------------------
export const MINIMO_PARA_AVALIAR = 3;

export function placarDosPagamentos(fretes, clientes, hoje, { por = 'cliente_id', desde = somarDias(hoje, -365) } = {}) {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const grupos = new Map();
  for (const bruto of fretes) {
    if (bruto.deleted_at || !bruto[por]) continue;
    const f = comContas(bruto);
    if (centavos(f.saldo_valor) <= 0) continue;
    const cliente = porId.get(f.cliente_id) ?? null;
    const prazo = Number.isInteger(cliente?.prazo_saldo_dias) ? cliente.prazo_saldo_dias : PRAZO_PADRAO_DO_SALDO;
    const base = f.data_descarga ?? f.data_carregamento;
    const vencimento = somarDias(base, prazo);
    if (!grupos.has(f[por])) {
      grupos.set(f[por], { id: f[por], pagos: 0, noPrazo: 0, somaDias: 0, maiorAtraso: 0, atrasados: 0, valorAtrasado: 0, maiorAtrasoAgora: 0 });
    }
    const g = grupos.get(f[por]);
    if (f.saldo_recebido_em) {
      if (f.saldo_recebido_em < desde) continue;
      g.pagos += 1;
      g.somaDias += Math.max(0, diasEntre(base, f.saldo_recebido_em));
      const atraso = diasEntre(vencimento, f.saldo_recebido_em);
      if (atraso <= 0) g.noPrazo += 1;
      else g.maiorAtraso = Math.max(g.maiorAtraso, atraso);
    } else if (vencimento < hoje) {
      g.atrasados += 1;
      g.valorAtrasado += centavos(f.saldo_valor);
      g.maiorAtrasoAgora = Math.max(g.maiorAtrasoAgora, diasEntre(vencimento, hoje));
    }
  }
  return [...grupos.values()]
    .filter((g) => g.pagos > 0 || g.atrasados > 0)
    .map((g) => {
      const r = {
        id: g.id,
        pagos: g.pagos,
        noPrazo: g.noPrazo,
        mediaDias: g.pagos > 0 ? Math.round(g.somaDias / g.pagos) : null,
        maiorAtraso: g.maiorAtraso,
        atrasados: g.atrasados,
        valorAtrasado: g.valorAtrasado / 100,
        maiorAtrasoAgora: g.maiorAtrasoAgora,
      };
      return { ...r, nota: notaDoPagador(r) };
    })
    // Os que mais atrasam primeiro
    .sort((a, b) => ORDEM_DA_NOTA[a.nota] - ORDEM_DA_NOTA[b.nota] || b.valorAtrasado - a.valorAtrasado || b.pagos - a.pagos);
}

// 'atrasa_muito' · 'atrasa' · 'em_dia' · 'poucos' (menos de 3 saldos pagos e nada atrasado)
const ORDEM_DA_NOTA = { atrasa_muito: 0, atrasa: 1, poucos: 2, em_dia: 3 };
export function notaDoPagador({ pagos, noPrazo, atrasados, maiorAtrasoAgora }) {
  if (maiorAtrasoAgora > 15) return 'atrasa_muito';
  if (pagos < MINIMO_PARA_AVALIAR) return atrasados > 0 ? 'atrasa' : 'poucos';
  const fracao = noPrazo / pagos;
  if (fracao < 0.5) return 'atrasa_muito';
  if (fracao < 0.8 || atrasados > 0) return 'atrasa';
  return 'em_dia';
}

export const NOTAS_DO_PAGADOR = {
  em_dia: { texto: 'Paga em dia', tom: 'ok' },
  atrasa: { texto: 'Às vezes atrasa', tom: 'alerta' },
  atrasa_muito: { texto: 'Costuma atrasar', tom: 'perigo' },
  poucos: { texto: 'Poucas cargas para avaliar', tom: '' },
};

// "Paga o saldo em 22 dias, em média · 8 de 10 no prazo · 1 atrasado agora (R$ 3.000,00)"
export function linhasDoPlacar(p) {
  const linhas = [];
  if (p.pagos > 0) {
    linhas.push(`Paga o saldo ${p.mediaDias === 0 ? 'no dia da descarga' : `${p.mediaDias} ${p.mediaDias === 1 ? 'dia' : 'dias'} depois da descarga`}, em média`);
    linhas.push(`${p.noPrazo} de ${p.pagos} ${p.pagos === 1 ? 'saldo pago' : 'saldos pagos'} no prazo${p.maiorAtraso > 0 ? ` · maior atraso: ${p.maiorAtraso} ${p.maiorAtraso === 1 ? 'dia' : 'dias'}` : ''}`);
  }
  if (p.atrasados > 0) {
    linhas.push(`${p.atrasados} ${p.atrasados === 1 ? 'saldo atrasado' : 'saldos atrasados'} agora (${dinheiro(p.valorAtrasado)}; o mais antigo há ${p.maiorAtrasoAgora} ${p.maiorAtrasoAgora === 1 ? 'dia' : 'dias'})`);
  }
  return linhas;
}
